import React from "react";
import { Card } from "./ui/card";
import ScoreGauge from "./ScoreGauge";
import { SlidersHorizontal, AlertCircle, Clock } from "lucide-react";

/**
 * Renders a single risk dimension. Handles the four possible states surfaced
 * by the backend `dimensions.<key>.status` envelope:
 *   • computed         → normal gauge
 *   • ai_disabled      → grey gauge + "AI disabilitata per l'azienda"
 *   • error            → red dash + tooltip
 *   • not_yet_computed → neutral dash
 */
export default function RiskDimensionCard({ label, dimension, mode = "risk", testId, size = 128 }) {
    const status = dimension?.status || "not_yet_computed";
    const score = dimension?.score;

    if (status === "computed" && score != null) {
        return (
            <Card className="p-4 flex flex-col items-center border-[#DCE3EE]" data-testid={testId}>
                <ScoreGauge score={score} label={label} size={size} mode={mode} />
            </Card>
        );
    }

    // Non-computed states: build a placeholder card with matching visual weight
    const config = {
        ai_disabled: {
            icon: <SlidersHorizontal className="w-6 h-6 text-[#0891B2]" />,
            bg: "bg-[#ECFEFF]",
            border: "border-[#A5F3FC]",
            text: "text-[#0E7490]",
            note: "AI disabilitata per l'azienda",
            badge: "AI OFF",
            badgeClass: "bg-[#0891B2] text-white",
        },
        error: {
            icon: <AlertCircle className="w-6 h-6 text-[#DC2626]" />,
            bg: "bg-[#FEF2F2]",
            border: "border-[#FCA5A5]",
            text: "text-[#B91C1C]",
            note: "Errore durante il calcolo",
            badge: "ERRORE",
            badgeClass: "bg-[#DC2626] text-white",
        },
        not_yet_computed: {
            icon: <Clock className="w-6 h-6 text-[#94A3B8]" />,
            bg: "bg-[#F8FAFC]",
            border: "border-[#DCE3EE]",
            text: "text-[#64748B]",
            note: "Non ancora calcolato",
            badge: null,
            badgeClass: "",
        },
    }[status] || {
        icon: <Clock className="w-6 h-6 text-[#94A3B8]" />,
        bg: "bg-[#F8FAFC]",
        border: "border-[#DCE3EE]",
        text: "text-[#64748B]",
        note: "Non disponibile",
        badge: null,
        badgeClass: "",
    };

    return (
        <Card
            className={`p-4 flex flex-col items-center ${config.border} ${config.bg}`}
            data-testid={testId}
            data-status={status}
        >
            <div
                className={`flex items-center justify-center rounded-full ${config.bg} border ${config.border}`}
                style={{ width: size, height: size }}
            >
                <div className="text-center">
                    <div className="mx-auto mb-1 flex items-center justify-center">{config.icon}</div>
                    <div className={`text-3xl font-semibold ${config.text}`}>—</div>
                </div>
            </div>
            <div className="mt-3 text-sm font-medium text-[#172033] text-center leading-tight">{label}</div>
            <div className={`text-[11px] mt-1 ${config.text} text-center leading-tight`}>{config.note}</div>
            {config.badge && (
                <div
                    className={`mt-2 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${config.badgeClass}`}
                    data-testid={`${testId}-badge`}
                >
                    {config.badge}
                </div>
            )}
        </Card>
    );
}
